<template>
  <div>
    <!-- Hero Section - Modern Creative Design -->
    <section class="about-hero">
      <!-- 3D DNA Animation Background -->
      <div class="dna-animation-bg">
        <div class="dna-helix">
          <div class="dna-strand-1"></div>
          <div class="dna-strand-2"></div>
          <div class="dna-strand-3"></div>
        </div>
      </div>
      
      <!-- Floating Elements -->
      <div class="floating-elements">
        <div class="floating-element fe-1"></div>
        <div class="floating-element fe-2"></div>
        <div class="floating-element fe-3"></div>
        <div class="floating-element fe-4"></div>
        <div class="floating-element fe-5"></div>
      </div>
      
      <!-- Gradient Overlay -->
      <div class="hero-gradient-overlay"></div>
      
      <!-- Content Container -->
      <div class="container position-relative">
        <div class="row hero-content-row">
          <!-- Left Column - Title Section -->
          <div class="col-lg-6 hero-text-column" data-aos="fade-right">
            <div class="hero-tag-pill">
              <span class="hero-tag-icon"><i class="bi bi-lightbulb"></i></span>
              <span class="hero-tag-text">WHO WE ARE</span>
            </div>
            
            <h1 class="hero-title">
              <span class="hero-title-about">About</span>
              <div class="hero-title-main">
                <span class="text-gradient">PEIST</span>
                <span class="hero-title-biotech">Biotech IP Consulting</span>
              </div>
            </h1>
            
            <div class="hero-description">
              <p>Pioneering intellectual property strategies at the intersection of science, law, and business.</p>
            </div>
            
            <div class="hero-stats-row">
              <div class="hero-stat">
                <div class="stat-number">25+</div>
                <div class="stat-label">Years Experience</div>
              </div>
              <div class="hero-stat">
                <div class="stat-number">100+</div>
                <div class="stat-label">Patents Secured</div>
              </div>

            </div>
          </div>
          
          <!-- Right Column - 3D Visual Element -->
          <div class="col-lg-6 hero-visual-column" data-aos="fade-left">
            <div class="hero-visual-element">
              <div class="hero-visual-inner">
                <div class="hero-visual-circle"></div>
                <div class="hero-visual-grid"></div>
                <div class="hero-visual-dots"></div>
                <div class="hero-visual-icon">
                  <i class="bi bi-shield-check"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Bottom Wave -->
      <div class="hero-wave">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path fill="#ffffff" fill-opacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,90.7C672,85,768,107,864,128C960,149,1056,171,1152,165.3C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
    </section>

    <!-- Mission Section - Modern Design -->
    <section class="mission-section" id="mission">
      <!-- Background Elements -->
      <div class="mission-bg-elements">
        <div class="mission-bg-circle"></div>
        <div class="mission-bg-dots"></div>
        <div class="mission-bg-lines"></div>
      </div>
      
      <div class="container position-relative">
        <!-- Section Header -->
        <div class="row mb-5">
          <div class="col-lg-8 mx-auto text-center">
            <div class="section-badge" data-aos="fade-up">
              <span class="badge-icon"><i class="bi bi-bullseye"></i></span>
              <span class="badge-text">OUR MISSION</span>
            </div>
            <h2 class="mission-title" data-aos="fade-up" data-aos-delay="100">
              Protecting <span class="text-gradient">Innovation</span> at the Frontier of Science
            </h2>
          </div>
        </div>
        
        <!-- Mission Content -->
        <div class="row align-items-stretch">
          <!-- Left Column: Mission Statement -->
          <div class="col-lg-6 mb-4 mb-lg-0">
            <div class="mission-card" data-aos="fade-right">
              <div class="mission-card-inner">
                <div class="mission-quote">
                  <div class="quote-icon"><i class="bi bi-quote"></i></div>
                  <p class="quote-text">Our mission is to safeguard the intellectual breakthroughs that drive the biotech and pharmaceutical industries forward.</p>
                </div>
                
                <div class="mission-content">
                  <p>PEIST Biotech IP Consulting is a specialized intellectual property consulting firm focused on the biotech and pharmaceutical industries. Founded by Kenneth Peist, our firm brings decades of experience in navigating the complex intersection of science, law, and business strategy.</p>
                  <p>We provide comprehensive IP services to help innovative companies protect their valuable discoveries, navigate regulatory pathways, and maximize the commercial potential of their intellectual property assets.</p>
                </div>
                
                <div class="mission-signature">
                  <div class="signature-line"></div>
                  <div class="signature-name">Kenneth Peist</div>
                  <div class="signature-title">Founder & Principal Consultant</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Column: Services Grid -->
          <div class="col-lg-6">
            <div class="services-grid">
              <!-- Service Card 1 -->
              <div class="service-card" data-aos="zoom-in" data-aos-delay="100">
                <div class="service-icon">
                  <i class="bi bi-clipboard-check"></i>
                </div>
                <h4 class="service-title">Patent Strategy</h4>
                <p class="service-description">Develop robust patent strategies to protect your core innovations and create barriers to competition.</p>
              </div>
              
              <!-- Service Card 2 -->
              <div class="service-card" data-aos="zoom-in" data-aos-delay="200">
                <div class="service-icon">
                  <i class="bi bi-shield-check"></i>
                </div>
                <h4 class="service-title">IP Portfolio Management</h4>
                <p class="service-description">Strategic management of your IP assets to maximize value and align with business objectives.</p>
              </div>
              
              <!-- Service Card 3 -->
              <div class="service-card" data-aos="zoom-in" data-aos-delay="300">
                <div class="service-icon">
                  <i class="bi bi-file-earmark-text"></i>
                </div>
                <h4 class="service-title">Regulatory Strategy</h4>
                <p class="service-description">Navigate complex regulatory pathways while protecting your intellectual property position.</p>
              </div>
              
              <!-- Service Card 4 -->
              <div class="service-card" data-aos="zoom-in" data-aos-delay="400">
                <div class="service-icon">
                  <i class="bi bi-briefcase"></i>
                </div>
                <h4 class="service-title">Due Diligence</h4>
                <p class="service-description">Comprehensive IP due diligence for investments, acquisitions, and strategic partnerships.</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Stats Counter Row -->
        <div class="row mt-5">
          <div class="col-12">
            <div class="stats-counter" data-aos="fade-up">
              <div class="counter-item">
                <div class="counter-number">25+</div>
                <div class="counter-label">Years Experience</div>
              </div>
              <div class="counter-divider"></div>
              <div class="counter-item">
                <div class="counter-number">100+</div>
                <div class="counter-label">Patents Secured</div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Founder Section - Modern Design -->
    <section class="founder-section" id="founder">
      <!-- Background Elements -->
      <div class="founder-bg-elements">
        <div class="founder-bg-shape-1"></div>
        <div class="founder-bg-shape-2"></div>
        <div class="founder-bg-pattern"></div>
      </div>
      
      <div class="container position-relative">
        <!-- Section Header -->
        <div class="row mb-5">
          <div class="col-lg-8 mx-auto text-center">
            <div class="section-badge" data-aos="fade-up">
              <span class="badge-icon"><i class="bi bi-person-circle"></i></span>
              <span class="badge-text">MEET THE FOUNDER</span>
            </div>
            <h2 class="section-title" data-aos="fade-up" data-aos-delay="100">
              Kenneth <span class="text-gradient">Peist</span>, Esq.
            </h2>
          </div>
        </div>
        
        <!-- Founder Content -->
        <div class="founder-content-container">
          <div class="row align-items-center">
            <!-- Left Column: Profile Image -->
            <div class="col-lg-5 mb-5 mb-lg-0">
              <div class="founder-profile-container" data-aos="fade-right">
                <div class="profile-card">
                  <div class="profile-image-wrapper">
                    <img src="/images/kenprofilepic.jpeg" alt="Kenneth Peist" class="profile-image">
                    <div class="profile-frame"></div>
                  </div>
                  
                  <div class="profile-badge">
                    <div class="badge-icon">
                      <i class="bi bi-award"></i>
                    </div>
                    <div class="badge-content">
                      <div class="badge-number">25+</div>
                      <div class="badge-text">Years Experience</div>
                    </div>
                  </div>
                  
                  <div class="profile-credentials">
                    <div class="credentials-item">
                      <div class="credentials-icon">
                        <i class="bi bi-mortarboard"></i>
                      </div>
                      <div class="credentials-text">J.D., Seton Hall University School of Law</div>
                    </div>
                    <div class="credentials-item">
                      <div class="credentials-icon">
                        <i class="bi bi-briefcase"></i>
                      </div>
                      <div class="credentials-text">Biotech IP Specialist</div>
                    </div>
                  </div>
                  
                  <div class="profile-social">
                    <a href="https://www.linkedin.com/in/kenneth-peist/" target="_blank" class="social-button linkedin">
                      <i class="bi bi-linkedin"></i>
                    </a>
                    <a href="mailto:ken@peistbiotechip.com" class="social-button email">
                      <i class="bi bi-envelope"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Right Column: Bio Content -->
            <div class="col-lg-7">
              <div class="founder-bio-container" data-aos="fade-left">
                <div class="bio-header">
                  <div class="bio-title">Founder & Principal Consultant</div>
                  <div class="bio-greeting">Hi, I'm Ken Peist, Esq.</div>
                </div>
                
                <div class="bio-content">
                  <p>With over 25 years of experience in intellectual property law for the biotech and pharmaceutical industries, I founded PEIST Biotech IP Consulting to provide specialized IP services to innovative companies developing life-changing therapeutics.</p>
                  
                  <p>I'm particularly passionate about working with companies developing novel therapeutics that have the potential to transform patient care. My approach combines scientific rigor, legal precision, and business pragmatism to create IP strategies that align with your company's long-term vision.</p>
                </div>
                
                <div class="bio-expertise">
                  <h4 class="expertise-title">Areas of Expertise</h4>
                  <div class="expertise-grid">
                    <div class="expertise-item">
                      <div class="expertise-icon">
                        <i class="bi bi-capsule"></i>
                      </div>
                      <div class="expertise-label">Pharmaceuticals</div>
                    </div>
                    <div class="expertise-item">
                      <div class="expertise-icon">
                        <i class="bi bi-virus"></i>
                      </div>
                      <div class="expertise-label">Biotechnology</div>
                    </div>
                    <div class="expertise-item">
                      <div class="expertise-icon">
                        <i class="bi bi-file-earmark-text"></i>
                      </div>
                      <div class="expertise-label">Patent Strategy</div>
                    </div>
                    <div class="expertise-item">
                      <div class="expertise-icon">
                        <i class="bi bi-diagram-3"></i>
                      </div>
                      <div class="expertise-label">IP Portfolio</div>
                    </div>
                  </div>
                </div>
                
                <div class="bio-cta">
                  <p>Feel free to connect with me to discuss how I can help your company develop a robust IP strategy.</p>
                  <a href="/contact" class="btn btn-primary btn-lg rounded-pill px-4">
                    <i class="bi bi-chat-dots me-2"></i>Schedule a Consultation
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Professional Journey Section -->
    <section class="journey-section" id="journey">
      <!-- Background Elements -->
      <div class="journey-bg-elements">
        <div class="journey-bg-shape-1"></div>
        <div class="journey-bg-shape-2"></div>
        <div class="journey-bg-dots"></div>
      </div>
      
      <div class="container position-relative">
        <!-- Section Header -->
        <div class="row mb-3">
          <div class="col-lg-8 mx-auto text-center">
            <div class="section-badge mb-1" data-aos="fade-up">
              <span class="badge-icon"><i class="bi bi-briefcase"></i></span>
              <span class="badge-text">PROFESSIONAL JOURNEY</span>
            </div>
            <h2 class="section-title mt-0 mb-2" data-aos="fade-up">
              A Career Built on <span class="text-gradient">Excellence</span>
            </h2>
            <p class="section-subtitle mb-0" data-aos="fade-up">
              Over 25 years of experience in intellectual property law and strategy
            </p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Journey Timeline Content -->
    <section class="professional-journey-content mt-0 pt-0" style="padding-top: 0 !important; padding-bottom: 60px;">
      <div class="container">
        <div class="career-section" data-aos="fade-up">
          <div class="career-timeline">
            <!-- Current Position -->
            <div class="timeline-item" data-aos="fade-up">
              <div class="timeline-dot active"></div>
              <div class="timeline-date">2023 - Present</div>
              <div class="timeline-content">
                <div class="timeline-card">
                  <div class="timeline-card-header">
                    <div class="company-logo-container">
                      <div class="company-logo-placeholder">
                        <i class="bi bi-building"></i>
                      </div>
                    </div>
                    <div>
                      <h4>Owner & Principal Consultant</h4>
                      <p class="company-name">PEIST Biotech IP Consulting LLC</p>
                    </div>
                  </div>
                  <div class="timeline-card-body">
                    <p>Intellectual Property consultant for former executives from Dermavant Sciences and Roivant Sciences. Assisting companies in "stealth mode" with developing IP strategies to maximize exclusivity of innovative therapeutics.</p>
                    <div class="skills-container">
                      <span class="skill-tag">IP Law</span>
                      <span class="skill-tag">FDA Regulatory</span>
                      <span class="skill-tag">Licensing</span>
                      <span class="skill-tag">M&A</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Dermavant Sciences -->
            <div class="timeline-item" data-aos="fade-up" data-aos-delay="100">
              <div class="timeline-dot"></div>
              <div class="timeline-date">2018 - 2025</div>
              <div class="timeline-content">
                <div class="timeline-card">
                  <div class="timeline-card-header">
                    <div class="company-logo-container">
                      <div class="company-logo-placeholder">
                        <i class="bi bi-building"></i>
                      </div>
                    </div>
                    <div>
                      <h4>VP, Intellectual Property</h4>
                      <p class="company-name">Dermavant Sciences</p>
                      <p class="company-subtitle">Subsidiary of Roivant Sciences, acquired by Organon</p>
                    </div>
                  </div>
                  <div class="timeline-card-body">
                    <p>Reported to Dermavant's General Counsel, responsible for oversight of Dermavant's Intellectual Property portfolio and providing legal support related to IP law, FDA regulatory law, and contracts.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Roivant Sciences -->
            <div class="timeline-item" data-aos="fade-up" data-aos-delay="200">
              <div class="timeline-dot"></div>
              <div class="timeline-date">2016 - 2018</div>
              <div class="timeline-content">
                <div class="timeline-card">
                  <div class="timeline-card-header">
                    <div class="company-logo-container">
                      <img src="/images/company-logos/roivant_sciences_inc__logo.jpeg" alt="Roivant Sciences Logo" class="company-logo">
                    </div>
                    <div>
                      <h4>Senior Partner & IP Counsel</h4>
                      <p class="company-name">Roivant Sciences</p>
                    </div>
                  </div>
                  <div class="timeline-card-body">
                    <p>Responsible for oversight of Roivant's IP portfolio and conducting asset evaluation and acquisition due diligence. Provided legal support related to IP and FDA regulatory law.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Amicus Therapeutics -->
            <div class="timeline-item" data-aos="fade-up" data-aos-delay="300">
              <div class="timeline-dot"></div>
              <div class="timeline-date">2007 - 2016</div>
              <div class="timeline-content">
                <div class="timeline-card">
                  <div class="timeline-card-header">
                    <div class="company-logo-container">
                      <img src="/images/company-logos/amicus_therapeutics_logo.jpeg" alt="Amicus Therapeutics Logo" class="company-logo">
                    </div>
                    <div>
                      <h4>VP, Legal & Intellectual Property</h4>
                      <p class="company-name">Amicus Therapeutics, Inc.</p>
                    </div>
                  </div>
                  <div class="timeline-card-body">
                    <p>Worked with executive leadership to ensure compliance with corporate policies and regulations. Supported stock offerings totaling over $400M. Managed legal team and oversaw due diligence for acquisitions.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- View More Experience Button -->
            <div class="timeline-view-more" data-aos="fade-up" data-aos-delay="400">
              <button class="btn-view-more" type="button" data-bs-toggle="collapse" data-bs-target="#moreExperience" aria-expanded="false" aria-controls="moreExperience">
                <span class="btn-text">View Full Career History</span>
                <i class="bi bi-chevron-down"></i>
              </button>
            </div>
            
            <!-- Collapsible Earlier Experience -->
            <div class="collapse" id="moreExperience">
              <!-- Bristol-Myers Squibb -->
              <div class="timeline-item" data-aos="fade-up">
                <div class="timeline-dot"></div>
                <div class="timeline-date">2000 - 2007</div>
                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="timeline-card-header">
                      <div class="company-logo-container">
                        <img src="/images/company-logos/bristol_myers_squibb_logo.jpeg" alt="Bristol-Myers Squibb Logo" class="company-logo">
                      </div>
                      <div>
                        <h4>Counsel, IP and Regulatory</h4>
                        <p class="company-name">Bristol-Myers Squibb</p>
                      </div>
                    </div>
                    <div class="timeline-card-body">
                      <p>Held various roles including Counsel to Senior R&D Executives, Patent Counsel, and Associate Patent Counsel. Oversaw life cycle management, coordinating IP, Regulatory, Research and Business strategies.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Vitae Pharmaceuticals -->
              <div class="timeline-item" data-aos="fade-up">
                <div class="timeline-dot"></div>
                <div class="timeline-date">2005 - 2006</div>
                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="timeline-card-header">
                      <div class="company-logo-container">
                        <img src="/images/company-logos/vitae_pharmaceuticals_logo.jpeg" alt="Vitae Pharmaceuticals Logo" class="company-logo">
                      </div>
                      <div>
                        <h4>Senior Director of IP</h4>
                        <p class="company-name">Vitae Pharmaceuticals</p>
                      </div>
                    </div>
                    <div class="timeline-card-body">
                      <p>Managed patent portfolio and provided general legal advice as sole in-house counsel. Performed due diligences and coordinated IP issues with collaboration partners.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- ExxonMobil -->
              <div class="timeline-item" data-aos="fade-up">
                <div class="timeline-dot"></div>
                <div class="timeline-date">1998 - 2000</div>
                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="timeline-card-header">
                      <div class="company-logo-container">
                        <img src="/images/company-logos/exxonlogo.jpeg" alt="ExxonMobil Logo" class="company-logo">
                      </div>
                      <div>
                        <h4>Patent Attorney</h4>
                        <p class="company-name">ExxonMobil</p>
                      </div>
                    </div>
                    <div class="timeline-card-body">
                      <p>Prepared and prosecuted patent applications for Research and Engineering unit covering technologies including microbial bioremediation agents, catalysts, polymers, and fuel cell technologies.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- EPA -->
              <div class="timeline-item" data-aos="fade-up">
                <div class="timeline-dot"></div>
                <div class="timeline-date">1990 - 1998</div>
                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="timeline-card-header">
                      <div class="company-logo-container">
                        <img src="/images/company-logos/us_epa_logo.jpeg" alt="U.S. Environmental Protection Agency Logo" class="company-logo">
                      </div>
                      <div>
                        <h4>Team Leader & Legal Intern</h4>
                        <p class="company-name">U.S. Environmental Protection Agency</p>
                      </div>
                    </div>
                    <div class="timeline-card-body">
                      <p>As legal intern in NYC Office of Regional Counsel, supported Superfund litigation efforts. As Team Leader, oversaw a team of chemists performing analytical procedures for criminal and civil investigations.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Early Career -->
              <div class="timeline-item" data-aos="fade-up">
                <div class="timeline-dot"></div>
                <div class="timeline-date">1986 - 1990</div>
                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="timeline-card-header">
                      <div class="company-logo-container">
                        <div class="company-logo-placeholder">
                          <i class="bi bi-flask"></i>
                        </div>
                      </div>
                      <div>
                        <h4>Chemistry Supervisor</h4>
                        <p class="company-name">ENSECO & EMS Laboratories</p>
                      </div>
                    </div>
                    <div class="timeline-card-body">
                      <p>Supervised chemistry departments, developed analytical methods, and optimized performance of sophisticated instrumentation. Coordinated quality management programs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- End Professional Journey Content -->

    <!-- Education Section -->
    <section class="education-section">
      <div class="container">
        <div class="education-wrapper">
          <h4 class="section-title mb-4">Education <span class="text-gradient">& Credentials</span></h4>
          <div class="row">
            <div class="col-md-6">
              <div class="education-card" data-aos="fade-up">
                <div class="education-icon">
                  <i class="bi bi-mortarboard"></i>
                </div>
                <div class="education-content">
                  <h5>J.D., Intellectual Property, Environmental and Corporate Law</h5>
                  <p>Seton Hall University School of Law (1994 - 1998)</p>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="education-card" data-aos="fade-up" data-aos-delay="100">
                <div class="education-icon">
                  <i class="bi bi-mortarboard"></i>
                </div>
                <div class="education-content">
                  <h5>B.S., Biology, Minor: Chemistry</h5>
                  <p>Old Dominion University (1981 - 1986)</p>
                  <small>Activities: ATO Fraternity, Sports Promotions Department</small>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-md-6">
              <div class="education-card" data-aos="fade-up" data-aos-delay="200">
                <div class="education-icon">
                  <i class="bi bi-award"></i>
                </div>
                <div class="education-content">
                  <h5>Certificate, Renewable Energy and Green Building Entrepreneurship</h5>
                  <p>Duke University (Oct 2020 - Dec 2020)</p>
                  <small>Skills: Environmental Entrepreneurship, LEED Certified Buildings, Renewable Energy Systems</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Services Section -->
    <section class="services-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-8 mx-auto text-center">
            <h6 class="section-subtitle" data-aos="fade-up">OUR EXPERTISE</h6>
            <h2 class="section-title" data-aos="fade-up" data-aos-delay="100">Comprehensive <span class="text-gradient">IP Services</span></h2>
            <div class="section-line" data-aos="fade-up" data-aos-delay="200"></div>
            <p class="section-text" data-aos="fade-up" data-aos-delay="300">
              We offer a full range of intellectual property services tailored to the unique needs of biotech and pharmaceutical companies.
              We work closely with our clients to develop tailored IP strategies that align with their business objectives and maximize the value of their innovations.
            </p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section - Modern Design -->
    <section class="cta-section">
      <!-- Background Elements -->
      <div class="cta-bg-elements">
        <div class="cta-shape-1"></div>
        <div class="cta-shape-2"></div>
        <div class="cta-shape-3"></div>
        <div class="cta-dots"></div>
      </div>
      
      <div class="container position-relative">
        <div class="row">
          <div class="col-lg-8 mx-auto">
            <div class="cta-content text-center" data-aos="fade-up">
              <h2 class="cta-title">Ready to Protect Your <span class="text-highlight">Innovations</span>?</h2>
              <p class="cta-text">Contact us today to schedule a consultation and discover how our specialized IP consulting services can help secure your biotech and pharmaceutical innovations.</p>
              
              <div class="cta-features">
                <div class="cta-feature-item">
                  <div class="feature-icon">
                    <i class="bi bi-shield-check"></i>
                  </div>
                  <div class="feature-text">Strategic IP Protection</div>
                </div>
                <div class="cta-feature-item">
                  <div class="feature-icon">
                    <i class="bi bi-graph-up"></i>
                  </div>
                  <div class="feature-text">Maximize Value</div>
                </div>
                <div class="cta-feature-item">
                  <div class="feature-icon">
                    <i class="bi bi-lightbulb"></i>
                  </div>
                  <div class="feature-text">Secure Innovation</div>
                </div>
              </div>
              
              <a href="/contact" class="cta-btn">
                <i class="bi bi-calendar-check me-2"></i>
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AboutPage'
}
</script>

<style scoped>
/* Hero Section Styles - Modern Creative Design */
.about-hero {
  position: relative;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
  overflow: hidden;
  padding: 0 0 20px;
  min-height: 85vh;
  display: flex;
  align-items: center;
}

/* DNA Animation Background */
.dna-animation-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: 0.5;
}

.dna-helix {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(30deg);
  width: 100%;
  height: 100%;
  animation: rotateHelix 30s linear infinite;
}

@keyframes rotateHelix {
  0% { transform: translate(-50%, -50%) rotate(30deg); }
  100% { transform: translate(-50%, -50%) rotate(390deg); }
}

.dna-strand-1, .dna-strand-2, .dna-strand-3 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(10, 132, 255, 0.1), transparent);
  transform-origin: center;
}

.dna-strand-1 {
  transform: rotate(0deg) translateY(-200px);
  animation: moveDnaStrand1 8s linear infinite;
}

.dna-strand-2 {
  transform: rotate(120deg) translateY(-200px);
  animation: moveDnaStrand2 8s linear infinite;
}

.dna-strand-3 {
  transform: rotate(240deg) translateY(-200px);
  animation: moveDnaStrand3 8s linear infinite;
}

@keyframes moveDnaStrand1 {
  0% { transform: rotate(0deg) translateY(-200px); }
  100% { transform: rotate(360deg) translateY(-200px); }
}

@keyframes moveDnaStrand2 {
  0% { transform: rotate(120deg) translateY(-200px); }
  100% { transform: rotate(480deg) translateY(-200px); }
}

@keyframes moveDnaStrand3 {
  0% { transform: rotate(240deg) translateY(-200px); }
  100% { transform: rotate(600deg) translateY(-200px); }
}

/* Floating Elements */
.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.floating-element {
  position: absolute;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.05), rgba(76, 194, 255, 0.1));
  border-radius: 50%;
  opacity: 0.6;
}

.fe-1 {
  top: 15%;
  left: 10%;
  width: 300px;
  height: 300px;
  animation: float1 20s ease-in-out infinite;
}

.fe-2 {
  top: 60%;
  left: 15%;
  width: 150px;
  height: 150px;
  animation: float2 15s ease-in-out infinite;
}

.fe-3 {
  top: 25%;
  right: 10%;
  width: 200px;
  height: 200px;
  animation: float3 18s ease-in-out infinite;
}

.fe-4 {
  bottom: 20%;
  right: 20%;
  width: 120px;
  height: 120px;
  animation: float4 12s ease-in-out infinite;
}

.fe-5 {
  top: 40%;
  left: 40%;
  width: 80px;
  height: 80px;
  animation: float5 10s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, 20px); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-20px, 30px); }
}

@keyframes float3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, -20px); }
}

@keyframes float4 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, -15px); }
}

@keyframes float5 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(15px, 10px); }
}

/* Gradient Overlay */
.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.8) 0%, rgba(240, 248, 255, 0.9) 100%);
  z-index: 2;
}

/* Content Container */
.container.position-relative {
  z-index: 3;
  padding: 120px 0;
}

.hero-content-row {
  min-height: 60vh;
  align-items: center;
}

/* Left Column - Title Section */
.hero-text-column {
  padding-right: 2rem;
}

.hero-tag-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(10, 132, 255, 0.1);
  border-radius: 50px;
  padding: 8px 16px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(10, 132, 255, 0.1);
  border: 1px solid rgba(10, 132, 255, 0.2);
}

.hero-tag-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #0a84ff;
  border-radius: 50%;
  color: white;
  margin-right: 8px;
  font-size: 0.8rem;
}

.hero-tag-text {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: #0a84ff;
  text-transform: uppercase;
}

.hero-title {
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #333;
}

.hero-title-about {
  display: block;
  font-size: 2.5rem;
  color: #555;
  margin-bottom: 0.5rem;
}

.hero-title-main {
  display: block;
}

.hero-title-biotech {
  color: #333;
}

.hero-description {
  margin-bottom: 2.5rem;
}

.hero-description p {
  font-size: 1.3rem;
  line-height: 1.7;
  color: #555;
  margin-bottom: 0;
}

/* Stats Row */
.hero-stats-row {
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
}

.hero-stat {
  text-align: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* Right Column - 3D Visual Element */
.hero-visual-column {
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-visual-element {
  position: relative;
  width: 400px;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-visual-inner {
  position: relative;
  width: 100%;
  height: 100%;
}

.hero-visual-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.1), rgba(76, 194, 255, 0.2));
  animation: pulseCircle 4s ease-in-out infinite;
}

@keyframes pulseCircle {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  50% { transform: translate(-50%, -50%) scale(1.05); opacity: 0.8; }
}

.hero-visual-grid {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 250px;
  height: 250px;
  border: 1px solid rgba(10, 132, 255, 0.2);
  animation: rotateGrid 20s linear infinite;
}

@keyframes rotateGrid {
  0% { transform: translate(-50%, -50%) rotate(45deg); }
  100% { transform: translate(-50%, -50%) rotate(405deg); }
}

.hero-visual-dots {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(10, 132, 255, 0.2) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.5;
}

.hero-visual-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  box-shadow: 0 10px 30px rgba(10, 132, 255, 0.3);
  z-index: 1;
}

/* Bottom Wave */
.hero-wave {
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  line-height: 0;
  z-index: 4;
}

/* Responsive Styles */
@media (max-width: 1199px) {
  .hero-title {
    font-size: 4rem;
  }
  
  .hero-visual-element {
    width: 350px;
    height: 350px;
  }
  
  .hero-visual-circle {
    width: 250px;
    height: 250px;
  }
}

@media (max-width: 991px) {
  .container.position-relative {
    padding: 80px 0;
  }
  
  .hero-text-column {
    padding-right: 0;
    margin-bottom: 3rem;
    text-align: center;
  }
  
  .hero-stats-row {
    justify-content: center;
  }
  
  .hero-visual-element {
    width: 300px;
    height: 300px;
  }
}

@media (max-width: 767px) {
  .about-hero {
    min-height: auto;
  }
  
  .hero-title {
    font-size: 3rem;
  }
  
  .hero-title-about {
    font-size: 2rem;
  }
  
  .hero-description p {
    font-size: 1.1rem;
  }
  
  .hero-stats-row {
    flex-wrap: wrap;
    gap: 1.5rem;
  }
  
  .hero-stat {
    flex: 0 0 calc(33.333% - 1rem);
  }
  
  .stat-number {
    font-size: 2rem;
  }
}

.text-gradient {
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Firm Section Styles */
.firm-section {
  padding: 100px 0;
  background-color: #fff;
  position: relative;
}

.firm-card {
  background-color: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  padding: 60px;
  overflow: hidden;
  position: relative;
}

.section-subtitle {
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 2px;
  color: #0a84ff;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;
}

.section-line {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  margin-bottom: 1.5rem;
}

.section-text {
  font-size: 1.05rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.7;
}

.firm-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-top: 30px;
}

.stat-item {
  flex: 1;
  min-width: 120px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #0a84ff;
  margin-bottom: 5px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.firm-image-container {
  position: relative;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.firm-image-bg {
  position: absolute;
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.1), rgba(76, 194, 255, 0.1));
  border-radius: 50%;
  z-index: 0;
}

.firm-services {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.service-item {
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.service-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.service-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: white;
  font-size: 1.2rem;
}

.service-text {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

/* Mission Section Styles - Modern Design */
.mission-section {
  position: relative;
  padding: 0 0 120px;
  background-color: #fff;
  overflow: hidden;
  margin-top: -120px;
  z-index: 5;
}

/* Background Elements */
.mission-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.mission-bg-circle {
  position: absolute;
  top: -150px;
  right: -150px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.03), rgba(76, 194, 255, 0.05));
  opacity: 0.7;
}

.mission-bg-dots {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(10, 132, 255, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.3;
}

.mission-bg-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(90deg, rgba(10, 132, 255, 0.05) 1px, transparent 1px),
                    linear-gradient(0deg, rgba(10, 132, 255, 0.05) 1px, transparent 1px);
  background-size: 100px 100px;
  opacity: 0.2;
}

/* Section Header */
.section-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.1), rgba(76, 194, 255, 0.2));
  border-radius: 30px;
  padding: 8px 16px;
  margin-bottom: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.section-badge .badge-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  box-shadow: 0 3px 8px rgba(10, 132, 255, 0.2);
}

.section-badge .badge-icon i {
  color: #fff;
  font-size: 0.9rem;
}

.section-badge .badge-text {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0a84ff;
  letter-spacing: 1.5px;
}

.badge-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #0a84ff;
  border-radius: 50%;
  color: white;
  margin-right: 8px;
  font-size: 0.8rem;
}

.badge-text {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: #0a84ff;
  text-transform: uppercase;
}

.mission-title {
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 2rem;
  line-height: 1.2;
}

/* Mission Card */
.mission-card {
  height: 100%;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.mission-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.mission-card-inner {
  padding: 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.mission-quote {
  margin-bottom: 30px;
  position: relative;
}

.quote-icon {
  position: absolute;
  top: -15px;
  left: -10px;
  font-size: 3rem;
  color: rgba(10, 132, 255, 0.1);
  z-index: 0;
}

.quote-text {
  position: relative;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
  color: #333;
  font-style: italic;
  z-index: 1;
}

.mission-content {
  flex: 1;
}

.mission-content p {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.mission-signature {
  margin-top: auto;
  padding-top: 20px;
}

.signature-line {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  margin-bottom: 15px;
}

.signature-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
}

.signature-title {
  font-size: 0.9rem;
  color: #666;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  height: 100%;
}

.service-card {
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 30px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 0;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  transition: height 0.3s ease;
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
}

.service-card:hover::before {
  height: 100%;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(10, 132, 255, 0.2);
}

.service-icon i {
  color: #fff;
  font-size: 1.5rem;
}

.service-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.service-description {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 0;
}

/* Stats Counter */
.stats-counter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f8f9fa, #f0f8ff);
  border-radius: 20px;
  padding: 30px 50px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.stats-counter::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
}

.counter-item {
  text-align: center;
  position: relative;
  z-index: 1;
}

.counter-number {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 5px;
  line-height: 1;
}

.counter-label {
  font-size: 0.9rem;
  color: #555;
  font-weight: 600;
}

.counter-divider {
  width: 1px;
  height: 50px;
  background: linear-gradient(to bottom, transparent, rgba(10, 132, 255, 0.2), transparent);
}

/* Responsive Styles */
@media (max-width: 1199px) {
  .mission-title {
    font-size: 2.5rem;
  }
  
  .services-grid {
    gap: 20px;
  }
  
  .service-card {
    padding: 25px;
  }
}

@media (max-width: 991px) {
  .mission-section {
    padding: 80px 0;
  }
  
  .services-grid {
    margin-top: 40px;
  }
  
  .stats-counter {
    flex-wrap: wrap;
    justify-content: center;
    gap: 40px;
    padding: 30px;
  }
  
  .counter-divider {
    display: none;
  }
  
  .counter-item {
    flex: 0 0 calc(50% - 20px);
  }
}

@media (max-width: 767px) {
  .mission-title {
    font-size: 2rem;
  }
  
  .quote-text {
    font-size: 1.3rem;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .counter-number {
    font-size: 2.5rem;
  }
}

/* Founder Section Styles - Modern Design */
.founder-section {
  position: relative;
  padding: 120px 0;
  background-color: #f8f9fa;
  overflow: hidden;
}

/* Background Elements */
.founder-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.founder-bg-shape-1 {
  position: absolute;
  top: -100px;
  left: -100px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.03), rgba(76, 194, 255, 0.05));
  opacity: 0.7;
}

.founder-bg-shape-2 {
  position: absolute;
  bottom: -150px;
  right: -150px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.02), rgba(76, 194, 255, 0.04));
  opacity: 0.5;
}

.founder-bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(10, 132, 255, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.2;
}

/* Section Header */
.section-title {
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 2rem;
  line-height: 1.2;
}

/* Founder Content Container */
.founder-content-container {
  position: relative;
  z-index: 1;
}

/* Profile Card */
.founder-profile-container {
  position: relative;
}

.profile-card {
  position: relative;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  padding: 30px;
  overflow: hidden;
}

.profile-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
}

.profile-image-wrapper {
  position: relative;
  width: 100%;
  margin-bottom: 30px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.profile-image {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.5s ease;
}

.profile-image-wrapper:hover .profile-image {
  transform: scale(1.03);
}

.profile-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 5px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  pointer-events: none;
}

.profile-badge {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa, #f0f8ff);
  border-radius: 15px;
  padding: 15px 20px;
  margin-bottom: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.profile-badge .badge-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.profile-badge .badge-icon i {
  color: #fff;
  font-size: 1.5rem;
}

.profile-badge .badge-content {
  flex: 1;
}

.profile-badge .badge-number {
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 5px;
}

.profile-badge .badge-text {
  font-size: 0.9rem;
  color: #555;
  font-weight: 600;
}

/* Credentials */
.profile-credentials {
  margin-bottom: 25px;
}

.credentials-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.credentials-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.credentials-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(10, 132, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.credentials-icon i {
  color: #0a84ff;
  font-size: 1.2rem;
}

.credentials-text {
  font-size: 0.95rem;
  color: #555;
  font-weight: 600;
}

/* Social Buttons */
.profile-social {
  display: flex;
  gap: 15px;
}

.social-button {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.social-button i {
  font-size: 1.3rem;
  transition: all 0.3s ease;
}

.social-button.linkedin {
  background: linear-gradient(135deg, #0077B5, #0e94e0);
}

.social-button.email {
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
}

.social-button i {
  color: #fff;
}

.social-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

/* Bio Container */
.founder-bio-container {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  padding: 40px;
  height: 100%;
}

.bio-header {
  margin-bottom: 30px;
}

.bio-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 15px;
}

.bio-greeting {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 15px;
}

.bio-greeting::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #0a84ff, #4cc2ff);
}

.bio-content {
  margin-bottom: 30px;
}

.bio-content p {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.7;
  margin-bottom: 20px;
}

/* Expertise Section */
.bio-expertise {
  margin-bottom: 30px;
}

.expertise-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
}

.expertise-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.expertise-item {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa, #f0f8ff);
  border-radius: 12px;
  padding: 15px;
  transition: all 0.3s ease;
}

.expertise-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}

.expertise-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.expertise-icon i {
  color: #fff;
  font-size: 1.2rem;
}

.expertise-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #555;
}

.expertise.badge-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  box-shadow: 0 3px 8px rgba(10, 132, 255, 0.2);
}

.badge-icon i {
  color: #fff;
  font-size: 0.9rem;
}

.badge-text {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0a84ff;
  letter-spacing: 1.5px;
}

/* CTA Section */
.bio-cta {
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.05), rgba(76, 194, 255, 0.1));
  border-radius: 15px;
  padding: 25px;
  text-align: center;
}

.bio-cta p {
  font-size: 1.05rem;
  color: #555;
  margin-bottom: 20px;
}

/* Responsive Styles */
@media (max-width: 1199px) {
  .section-title {
    font-size: 2.5rem;
  }
  
  .founder-bio-container {
    padding: 30px;
  }
  
  .bio-greeting {
    font-size: 1.8rem;
  }
  
  .expertise-grid {
    gap: 15px;
  }
}

@media (max-width: 991px) {
  .founder-section {
    padding: 80px 0;
  }
  
  .profile-card {
    max-width: 450px;
    margin: 0 auto;
  }
  
  .founder-bio-container {
    margin-top: 30px;
  }
}

@media (max-width: 767px) {
  .section-title {
    font-size: 2rem;
  }
  
  .bio-greeting {
    font-size: 1.6rem;
  }
  
  .expertise-grid {
    grid-template-columns: 1fr;
  }
  
  .bio-cta {
    padding: 20px;
  }
}

/* Career Section Styles */
.career-section-wrapper {
  padding: 80px 0;
  background-color: #fff;
}

.professional-journey-content {
  padding: 60px 0 100px;
  background-color: #fff;
}

.career-section {
  margin-bottom: 40px;
}

.career-timeline {
  position: relative;
  padding-left: 30px;
}

.career-timeline::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 2px;
  background: linear-gradient(to bottom, var(--primary), rgba(var(--primary-rgb), 0.1));
}

.timeline-item {
  position: relative;
  padding-bottom: 50px;
}

.timeline-dot {
  position: absolute;
  left: -9px;
  top: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid #0a84ff;
  z-index: 1;
}

.timeline-dot.active {
  background-color: #0a84ff;
  box-shadow: 0 0 0 4px rgba(10, 132, 255, 0.2);
}

.timeline-date {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0a84ff;
  margin-bottom: 10px;
  display: inline-block;
  padding: 5px 15px;
  background-color: rgba(10, 132, 255, 0.1);
  border-radius: 20px;
}

.timeline-content {
  padding-left: 20px;
}

.timeline-card {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
}

.timeline-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.timeline-card-header {
  padding: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
}

.company-logo-container {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  margin-right: 15px;
  flex-shrink: 0;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
}

.company-logo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(10, 132, 255, 0.1), rgba(76, 194, 255, 0.1));
  color: #0a84ff;
  font-size: 1.5rem;
}

.company-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
}

.timeline-card-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.company-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #0a84ff;
  margin-bottom: 5px;
}

.company-subtitle {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0;
}

.timeline-card-body {
  padding: 20px;
}

.timeline-card-body p {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.6;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-tag {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 20px;
  background-color: rgba(10, 132, 255, 0.1);
  color: #0a84ff;
}

.timeline-view-more {
  text-align: center;
  margin: 20px 0;
}

.btn-view-more {
  background: none;
  border: none;
  padding: 10px 25px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #0a84ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 30px;
  background-color: rgba(10, 132, 255, 0.1);
  transition: all 0.3s ease;
}

.btn-view-more:hover {
  background-color: rgba(10, 132, 255, 0.2);
}

.btn-view-more i {
  transition: transform 0.3s ease;
}

.btn-view-more[aria-expanded="true"] i {
  transform: rotate(180deg);
}

/* Education Section Styles */
.education-section {
  padding: 80px 0;
  background-color: #f8f9fa;
}

.education-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.education-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.education-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.education-icon {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.education-content h5 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.education-content p {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0;
}

/* CTA Section Styles - Modern Design */
.cta-section {
  padding: 120px 0;
  position: relative;
  background: linear-gradient(135deg, #0a84ff, #4cc2ff);
  overflow: hidden;
}

/* Background Elements */
.cta-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.cta-shape-1 {
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
  top: -250px;
  left: -100px;
}

.cta-shape-2 {
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
  bottom: -200px;
  right: -100px;
}

.cta-shape-3 {
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.cta-dots {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.3;
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-title {
  font-size: 3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.5rem;
  text-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.text-highlight {
  position: relative;
  display: inline-block;
  color: #fff;
  z-index: 1;
}

.text-highlight::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 10px;
  background-color: rgba(255, 255, 255, 0.3);
  z-index: -1;
  border-radius: 10px;
}

.cta-text {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

/* Feature Items */
.cta-features {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 2.5rem;
}

.cta-feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.feature-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.feature-icon i {
  font-size: 1.5rem;
  color: #fff;
}

.feature-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
}

.cta-btn {
  padding: 15px 35px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  background-color: #fff;
  color: #0a84ff;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.cta-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
  transform: translateX(-100%);
  transition: transform 0.6s ease;
  z-index: -1;
}

.cta-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  color: #0a84ff;
}

.cta-btn:hover::before {
  transform: translateX(100%);
}

/* Responsive Styles */
@media (max-width: 991px) {
  .cta-section {
    padding: 80px 0;
  }
  
  .cta-title {
    font-size: 2.5rem;
  }
  
  .cta-text {
    font-size: 1.1rem;
  }
  
  .cta-features {
    flex-wrap: wrap;
    gap: 20px;
  }
}

@media (max-width: 767px) {
  .cta-title {
    font-size: 2rem;
  }
  
  .cta-text {
    font-size: 1rem;
  }
  
  .feature-icon {
    width: 50px;
    height: 50px;
  }
  
  .feature-icon i {
    font-size: 1.2rem;
  }
  
  .feature-text {
    font-size: 0.85rem;
  }
  
  .cta-btn {
    padding: 12px 25px;
    font-size: 1rem;
  }
}

/* Responsive Styles */
@media (max-width: 991.98px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .firm-card {
    padding: 40px;
  }
  
  .founder-content-wrapper {
    padding: 40px;
  }
  
  .founder-image-wrapper {
    min-height: 400px;
  }
}

@media (max-width: 767.98px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .firm-services {
    grid-template-columns: 1fr;
  }
  
  .stat-item {
    min-width: 100%;
  }
  
  .founder-experience-badge {
    width: 100px;
    height: 100px;
  }
  
  .badge-inner {
    width: 90px;
    height: 90px;
  }
  
  .badge-number {
    font-size: 1.5rem;
  }
  
  .cta-title {
    font-size: 2rem;
  }
}
</style>
